import type { OrganisationLocation, OrganisationLocationWrite } from '@/hooks/use-organisations'
import { formatEntityLocation } from '@/utils/format-entity-location'

export type OrganisationLocationLike = {
  city?: string | null
  country?: string | null
  description?: string | null
}

export function organisationLocations(
  org?: { locations?: OrganisationLocation[] | null } | null,
): OrganisationLocation[] {
  return Array.isArray(org?.locations) ? org.locations : []
}

/** City and country, or the description if there is no place name. */
export function formatOrganisationLocationPlace(location: OrganisationLocationLike): string {
  const place = formatEntityLocation(location.city, location.country)
  const description = location.description?.trim() ?? ''
  return place || description
}

/** Place plus optional description, e.g. "Melbourne, AU — Headquarters". */
export function formatOrganisationLocationLine(location: OrganisationLocationLike): string {
  const place = formatEntityLocation(location.city, location.country)
  const description = location.description?.trim() ?? ''
  if (place && description) return `${place} — ${description}`
  return place || description
}

export function formatOrganisationLocationsSummary(
  locations: OrganisationLocationLike[] | null | undefined,
): string {
  return (locations ?? [])
    .map((location) => formatOrganisationLocationPlace(location))
    .filter(Boolean)
    .join(' · ')
}

export function isOrganisationLocationBlank(location: OrganisationLocationLike): boolean {
  return !(
    location.city?.trim() ||
    location.country?.trim() ||
    location.description?.trim()
  )
}

export function organisationLocationsForSave(
  locations: OrganisationLocationLike[],
): OrganisationLocationWrite[] {
  return locations
    .filter((location) => !isOrganisationLocationBlank(location))
    .map((location) => ({
      city: location.city?.trim() ?? '',
      country: location.country?.trim() ?? '',
      description: location.description?.trim() ?? '',
    }))
}
